import { useState } from 'react';
import {
  PROVIDERS,
  type ProxyLab,
  type ProxyModel,
} from '../../hooks/useProxyAdmin';
import {
  optionsWith,
  READ_MODES,
  type ReadMode,
} from '../../utils/middlemanRules';
import {
  CheckField,
  Section,
  SelectField,
  TextAreaField,
  TextField,
} from './FormFields';
import { FormDialog } from './FormDialog';
import {
  ADVANCED_FIELDS,
  buildCreateBody,
  buildPatch,
  COST_FIELDS,
  draftFromModel,
  INT_FIELDS,
  describePolicy,
  modelFaults,
  parseTags,
  PUBLIC_GROUP,
  type FaultField,
  type ModelDraft,
  type Tri,
} from './modelDraft';

/** Where in the form each repairable setting is edited. */
const FAULT_SECTION: Record<FaultField, string> = {
  required_anthropic_betas: 'Anthropic beta header above',
  read_policy: 'Transcript read access above',
  reader_tags: 'Transcript read access above',
};

// A label per mode middleman has (the Record fails to compile if one lacks one).
const READ_MODE_LABELS: Record<ReadMode, string> = {
  provider: 'Same provider’s models (default)',
  any: 'Any model',
  tags: 'Models with a given tag',
};
const READ_MODE_OPTIONS = READ_MODES.map(mode => ({
  value: mode,
  label: READ_MODE_LABELS[mode],
}));

const TRI_OPTIONS = (unrecorded: string, allowUnset: boolean) =>
  [
    { value: '' as Tri, label: unrecorded, disabled: !allowUnset },
    { value: 'true' as Tri, label: 'Yes' },
    { value: 'false' as Tri, label: 'No' },
  ] as const;

/** Add a model, or change one: common fields as inputs, the rest as JSON. */
export function ModelForm({
  model,
  labs,
  busy,
  onSubmit,
  onCancel,
}: {
  /** Null to add a model. */
  model: ProxyModel | null;
  /** Active labs to choose from; undefined while they load. */
  labs: ProxyLab[] | undefined;
  busy: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(() => draftFromModel(model));
  const [initial] = useState(() => JSON.stringify(draft));
  const [problem, setProblem] = useState<string | null>(null);
  const set = <K extends keyof ModelDraft>(key: K, value: ModelDraft[K]) =>
    setDraft(d => ({ ...d, [key]: value }));
  const creating = model === null;
  const faults = model ? modelFaults(model) : [];
  // Undefined while labs load, or for a lab that is no longer active.
  const labApi = labs?.find(lab => lab.name === draft.lab)?.api;
  // Beta flags only mean something on an Anthropic lab; still shown wherever
  // some are stored, so they can be cleared after a lab change.
  // When the lab's API isn't known (labs failed to load, or the lab is
  // inactive) it shows too, and middleman decides: hiding it would leave no
  // way to add a first requirement.
  const showBetas =
    labApi === undefined ||
    labApi === 'anthropic' ||
    draft.anthropic_betas.trim() !== '' ||
    faults.some(f => f.field === 'required_anthropic_betas');
  // The policy as entered, for the live description.
  const policy = {
    mode: draft.read_mode,
    tags: draft.read_mode === 'tags' ? parseTags(draft.read_tags) : [],
    zdr_only: draft.read_zdr_only,
  };
  const labNames = labs?.map(lab => lab.name) ?? [];
  const labOptions = [
    { value: '', label: 'Choose a lab…', disabled: true },
    ...labNames.map(name => ({ value: name, label: name })),
    // Keep a stored lab that is no longer active choosable, so the form shows it.
    ...(draft.lab && !labNames.includes(draft.lab)
      ? [{ value: draft.lab, label: `${draft.lab} (not active)` }]
      : []),
  ];
  const aliased =
    draft.danger_name.trim() !== '' &&
    draft.danger_name.trim() !==
      (model?.public_name ?? draft.public_name.trim());

  return (
    <FormDialog
      label={creating ? 'Add model' : `Edit ${model.public_name}`}
      title={
        creating ? (
          'Add model'
        ) : (
          <>
            Edit <span className="font-mono">{model.public_name}</span>
          </>
        )
      }
      dirty={JSON.stringify(draft) !== initial}
      busy={busy}
      problem={problem}
      onClose={onCancel}
      onSubmit={() => {
        const built = creating
          ? buildCreateBody(draft, labApi)
          : buildPatch(draft, model, labApi);
        if ('error' in built) {
          setProblem(built.error);
          return;
        }
        setProblem(null);
        onSubmit(built.body);
      }}
    >
      <Section legend="Identity">
        {creating && (
          <TextField
            label="Public name"
            required
            value={draft.public_name}
            onChange={v => set('public_name', v)}
            hint="The name callers request; it can't change later."
          />
        )}
        <TextField
          label="Group"
          required
          value={draft.group}
          placeholder="model-access-…"
          onChange={v => set('group', v)}
          hint="The model-access group that grants it."
        />
        {labs ? (
          <SelectField
            label="Lab"
            required
            value={draft.lab}
            options={labOptions}
            onChange={v => set('lab', v)}
          />
        ) : (
          <TextField
            label="Lab"
            required
            value={draft.lab}
            onChange={v => set('lab', v)}
          />
        )}
        <SelectField
          label="Provider"
          value={draft.provider}
          options={[
            {
              value: '',
              label: 'Unrecorded',
              disabled: !creating && model.provider !== null,
            },
            ...optionsWith<string>(PROVIDERS, draft.provider),
          ]}
          onChange={v => set('provider', v)}
          hint="The company that serves the model."
        />
      </Section>
      {showBetas && (
        <>
          <Section legend="Anthropic beta header">
            <TextField
              label="Required anthropic-beta flags"
              mono
              className="w-96"
              value={draft.anthropic_betas}
              placeholder="none"
              onChange={v => set('anthropic_betas', v)}
              hint="Comma- or space-separated; leave empty for no requirement."
            />
          </Section>
          <p className="mt-1 max-w-3xl text-muted">
            Every request to this model must then carry these flags in its{' '}
            <code>anthropic-beta</code> header: middleman refuses one that lacks
            any and never adds them, so callers have to send them.
          </p>
          {labApi !== undefined &&
            labApi !== 'anthropic' &&
            draft.anthropic_betas.trim() !== '' && (
              <p className="callout callout-warn mt-1">
                Only a lab served by the Anthropic API honours these flags, and
                this model’s lab uses <code>{labApi}</code>: clear them, or
                choose an Anthropic lab.
              </p>
            )}
        </>
      )}
      <Section legend="Data handling">
        <SelectField
          label="Zero data retention"
          required={creating}
          value={draft.is_zdr}
          options={TRI_OPTIONS('Choose…', false)}
          onChange={v => set('is_zdr', v)}
        />
        <SelectField
          label="Returns raw CoT"
          required={creating}
          value={draft.raw_cot}
          options={TRI_OPTIONS(creating ? 'Choose…' : 'Unrecorded', false)}
          onChange={v => set('raw_cot', v)}
          hint="Once recorded, raw CoT can be corrected but not reset to unrecorded."
        />
        <CheckField
          label="Dead"
          checked={draft.dead}
          onChange={v => set('dead', v)}
          hint="Middleman refuses every call to it and Hawk refuses new runs that use it; it's also hidden from model lists. Transcripts stay readable."
        />
        <CheckField
          label="Vision"
          checked={draft.vision}
          onChange={v => set('vision', v)}
        />
        <CheckField
          label="Stream"
          checked={draft.stream}
          onChange={v => set('stream', v)}
        />
      </Section>
      <Section legend="Transcript read access">
        <SelectField
          label="Who may read its transcripts"
          value={draft.read_mode}
          options={READ_MODE_OPTIONS}
          className="w-64"
          onChange={v => set('read_mode', v)}
          hint="Which reader models (scanners, transcript search) Hawk lets read this model's non-public transcripts."
        />
        {draft.read_mode === 'tags' && (
          <TextField
            label="Readers holding any of these tags"
            required
            mono
            className="w-64"
            value={draft.read_tags}
            placeholder="tag-a, tag-b"
            onChange={v => set('read_tags', v)}
            hint="Comma- or space-separated."
          />
        )}
        <CheckField
          label="Zero-data-retention readers only"
          checked={draft.read_zdr_only}
          onChange={v => set('read_zdr_only', v)}
          hint="Also require the reader model to be recorded as zero-data-retention."
        />
        <TextField
          label="Tags this model holds as a reader"
          mono
          className="w-64"
          value={draft.reader_tags}
          placeholder="none"
          onChange={v => set('reader_tags', v)}
          hint="Lets it read models whose policy names one of these tags. Everyone who can see this model sees its tags, so never name a model, lab or agreement in one."
        />
      </Section>
      <p className="mt-1 max-w-3xl text-muted">
        Readers allowed:{' '}
        <span className="text-ink">{describePolicy(policy)}</span>.
        {draft.group.trim() === PUBLIC_GROUP &&
          ` Hawk doesn’t restrict ${PUBLIC_GROUP} transcripts, so this model keeps the default policy.`}
      </p>
      <Section legend="Naming">
        <TextField
          label="Upstream (danger) name"
          value={draft.danger_name}
          placeholder={model?.public_name ?? 'same as public name'}
          onChange={v => set('danger_name', v)}
          hint="The name sent to the lab, when it differs from the public name."
        />
        <SelectField
          label="Details secret"
          required={creating && aliased}
          value={draft.are_details_secret}
          options={TRI_OPTIONS('Choose…', creating && !aliased)}
          onChange={v => set('are_details_secret', v)}
          hint="Secret hides the upstream name and details from everyone but admins."
        />
        <TextField
          label="Private name"
          value={draft.private_name}
          onChange={v => set('private_name', v)}
          hint="An alias for a public model; unlike the upstream name, never secret."
        />
      </Section>
      <Section legend="Prices (USD per million tokens)">
        {COST_FIELDS.map(([field, label]) => (
          <TextField
            key={field}
            label={label}
            inputMode="decimal"
            className="w-28"
            value={draft[field]}
            onChange={v => set(field, v)}
          />
        ))}
      </Section>
      <Section legend="Limits">
        {INT_FIELDS.map(([field, label]) => (
          <TextField
            key={field}
            label={label}
            inputMode="numeric"
            className="w-32"
            value={draft[field]}
            onChange={v => set(field, v)}
          />
        ))}
      </Section>
      <div className="mt-3">
        <TextAreaField
          label="Comments"
          value={draft.comments}
          onChange={v => set('comments', v)}
          rows={2}
        />
      </div>
      {faults.length > 0 && (
        <fieldset className="mt-3">
          <legend className="eyebrow">Malformed stored settings</legend>
          {faults.map(fault => (
            <div key={fault.field} className="callout callout-warn mt-1">
              <p>
                <b>{fault.label}</b>: {fault.message} {fault.effect}
              </p>
              <CheckField
                label={`Rewrite ${fault.field} from ${FAULT_SECTION[fault.field]}`}
                checked={draft.repair.includes(fault.field)}
                onChange={on =>
                  set(
                    'repair',
                    on
                      ? [...draft.repair, fault.field]
                      : draft.repair.filter(f => f !== fault.field)
                  )
                }
                hint="The form shows the cleaned value middleman reports, not what is stored, so saving it unchanged would otherwise be skipped."
              />
            </div>
          ))}
        </fieldset>
      )}
      <details className="mt-3" open={faults.length > 0}>
        <summary className="cursor-pointer text-muted">
          Advanced (JSON): {ADVANCED_FIELDS.length} more fields
        </summary>
        <div className="mt-2">
          <TextAreaField
            label="Advanced fields"
            mono
            rows={12}
            value={draft.advanced}
            onChange={v => set('advanced', v)}
            hint={
              <>
                Any of {ADVANCED_FIELDS.join(', ')}.{' '}
                {creating
                  ? 'Omitted fields take their defaults.'
                  : 'Omitted fields keep their value; null clears a nullable one.'}
              </>
            }
          />
        </div>
      </details>
    </FormDialog>
  );
}
